import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Replay, Visibility } from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { PageContainer } from '../Styles/AppStyles';
import useGetCustomData from '../Hooks/useGetCustomData';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import apiClient from '../Services/ApiClient';

interface LowStockFailedOrder {
  OID: string;
  POID: string | null;
  Sum_Amount: number;
  Created_at: string;
  Reason: string | null;
  locationName: string;
  employeeName: string;
  itemCount: number;
}

interface OrderItem {
  ID: number;
  PID: number;
  productName: string;
  productCode: string;
  orderedQty: number;
  unitPrice: number;
  amount: number;
  salesPricingUnit: string;
  currentStock: number;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const LowStockFailedOrdersPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [refreshKey, setRefreshKey] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOID, setSelectedOID] = useState<string | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // keyed by item.PID → raw string the user typed (parsed to number only on submit)
  const [editedStock, setEditedStock] = useState<Record<number, string>>({});

  const [retryingOIDs, setRetryingOIDs] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { columnSearches, setSearch, clearSearch } = useColumnSearches([
    'OID', 'locationName', 'employeeName', 'POID',
  ]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('pageSize', String(pageSize));
    if (refreshKey) params.set('_', String(refreshKey));
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    return `/api/failed-orders/low-stock?${params.toString()}`;
  }, [page, pageSize, refreshKey, columnSearches]);

  const [data, loading] = useGetCustomData(url);

  const orders: LowStockFailedOrder[] = data?.orders ?? [];
  const totalCount: number = data?.totalCount ?? 0;

  const handleViewItems = async (oid: string) => {
    setSelectedOID(oid);
    setModalOpen(true);
    setItemsLoading(true);
    setItems([]);
    setEditedStock({});
    try {
      const response = await apiClient(
        `/api/failed-orders/low-stock/${encodeURIComponent(oid)}/items`
      );
      const json = await response.json();
      setItems(json.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const handleRetry = async (oid: string, stockOverrides?: Record<number, number>) => {
    setRetryingOIDs((prev) => new Set(prev).add(oid));
    try {
      const hasOverrides = stockOverrides && Object.keys(stockOverrides).length > 0;
      const response = await apiClient(
        `/api/failed-orders/low-stock/${encodeURIComponent(oid)}/retry`,
        {
          method: 'POST',
          ...(hasOverrides ? { body: JSON.stringify({ stockOverrides }) } : {}),
        }
      );
      const json = await response.json();

      if (json.isSuccess) {
        setSnackbar({ open: true, message: `Order ${oid} retried successfully.`, severity: 'success' });
        setRefreshKey((k) => k + 1);
      } else {
        setSnackbar({ open: true, message: json.responseMessage || 'Retry failed.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Retry request failed.', severity: 'error' });
    } finally {
      setRetryingOIDs((prev) => {
        const next = new Set(prev);
        next.delete(oid);
        return next;
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOID(null);
    setItems([]);
    setEditedStock({});
  };

  const columns: ColumnDef<any>[] = [
    {
      field: '_actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const oid: string = params.row.OID;
        const isRetrying = retryingOIDs.has(oid);
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip title="View items">
              <IconButton size="small" onClick={() => handleViewItems(oid)}>
                <Visibility fontSize="small" sx={{ color: '#64748b' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Retry order">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleRetry(oid)}
                  disabled={isRetrying}
                >
                  {isRetrying
                    ? <CircularProgress size={16} />
                    : <Replay fontSize="small" sx={{ color: '#008490' }} />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: 'OID',
      headerName: 'Transaction ID',
      width: 160,
      renderHeader: () => <SearchableHeader field="OID" headerName="Transaction ID" searchValue={columnSearches.OID} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'Created_at',
      headerName: 'Order Date',
      width: 160,
    },
    {
      field: 'POID',
      headerName: 'PO / Job #',
      width: 130,
      renderHeader: () => <SearchableHeader field="POID" headerName="PO / Job #" searchValue={columnSearches.POID} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'locationName',
      headerName: 'Location',
      width: 220,
      renderHeader: () => <SearchableHeader field="locationName" headerName="Location" searchValue={columnSearches.locationName} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'employeeName',
      headerName: 'Employee',
      width: 150,
      renderHeader: () => <SearchableHeader field="employeeName" headerName="Employee" searchValue={columnSearches.employeeName} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'Sum_Amount',
      headerName: 'Amount',
      width: 110,
      renderCell: (params: GridRenderCellParams) =>
        currencyFormatter.format(params.value ?? 0),
    },
    {
      field: 'itemCount',
      headerName: 'Items',
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" />
      ),
    },
    {
      field: 'Reason',
      headerName: 'Failure Reason',
      width: 320,
      renderCell: (params: GridRenderCellParams) => {
        const message: string = params.value ?? '';
        if (!message) return null;
        return (
          <Tooltip title={message} placement="top-start">
            <Typography
              variant="body2"
              noWrap
              sx={{ fontSize: '0.8rem', color: '#d32f2f', cursor: 'default' }}
            >
              {message}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <Header title="Low Stock Failed Orders" count={totalCount} />

      <DataTable
        data={orders}
        columns={columns}
        title="Low Stock Failed Orders"
        pageKey="low-stock-failed-orders"
        loading={loading}
        serverSidePagination
        page={page}
        idField="OID"
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
      />

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pr: 6, fontSize: '1rem', fontWeight: 600 }}>
          Items — Transaction {selectedOID}
          <div style={{ position: 'absolute', right: 48, top: 8 }}>
            <Button
              size="small"
              startIcon={retryingOIDs.has(selectedOID ?? '') ? <CircularProgress size={14} /> : <Replay />}
              disabled={!selectedOID || retryingOIDs.has(selectedOID)}
              onClick={() => {
                if (!selectedOID) return;
                const overrides: Record<number, number> = {};
                for (const [pid, val] of Object.entries(editedStock)) {
                  const n = parseInt(val, 10);
                  if (!isNaN(n) && n >= 0) overrides[Number(pid)] = n;
                }
                handleRetry(selectedOID, Object.keys(overrides).length > 0 ? overrides : undefined);
              }}
              sx={{ color: '#008490', textTransform: 'none', fontWeight: 600 }}
            >
              Retry
            </Button>
          </div>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {itemsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <CircularProgress size={32} />
            </div>
          ) : items.length === 0 ? (
            <Typography sx={{ p: 3, color: '#64748b' }}>No items found.</Typography>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' } }}>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">Ordered</TableCell>
                  <TableCell align="center">In Stock</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const effectiveStock = item.PID in editedStock
                    ? (parseInt(editedStock[item.PID], 10) || 0)
                    : item.currentStock;
                  const isFailed = effectiveStock < item.orderedQty;
                  return (
                    <TableRow
                      key={item.ID}
                      sx={{
                        backgroundColor: isFailed ? '#fff5f5' : 'inherit',
                        '&:hover': { backgroundColor: isFailed ? '#ffe8e8' : '#f5f5f5' },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: isFailed ? 600 : 400,
                          color: isFailed ? '#c62828' : 'inherit',
                          fontSize: '0.82rem',
                        }}
                      >
                        {item.productName}
                        {isFailed && (
                          <Chip
                            label="Low Stock"
                            size="small"
                            sx={{
                              ml: 1,
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: '#ffcdd2',
                              color: '#c62828',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: '#555' }}>
                        {item.productCode}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.82rem' }}>
                        {item.orderedQty}
                        {item.salesPricingUnit ? ` ${item.salesPricingUnit}` : ''}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.82rem' }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={item.PID in editedStock ? editedStock[item.PID] : String(item.currentStock)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            setEditedStock((prev) => ({ ...prev, [item.PID]: raw }));
                          }}
                          style={{
                            width: 64,
                            textAlign: 'center',
                            border: `1px solid ${isFailed ? '#c62828' : '#ccc'}`,
                            borderRadius: 4,
                            padding: '3px 4px',
                            fontSize: '0.82rem',
                            fontWeight: isFailed ? 700 : 400,
                            color: isFailed ? '#c62828' : '#2e7d32',
                            background: isFailed ? '#fff5f5' : '#fff',
                            outline: 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                        {currencyFormatter.format(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                        {currencyFormatter.format(item.amount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default LowStockFailedOrdersPage;
